import { Link } from 'react-router-dom';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

export default function MembershipCTA() {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section ref={ref} className="py-20 bg-white">
      <div
        className="max-w-7xl mx-auto px-4 md:px-8 transition-all duration-700"
        style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(30px)' }}
      >
        {/* Section Label */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-full px-5 py-2 mb-4">
            <i className="ri-vip-crown-line text-amber-500"></i>
            <span className="text-sm font-bold text-amber-700">会员专属权益</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-3" style={{ fontFamily: "'Noto Serif SC', serif" }}>
            选择您的会员方案
          </h2>
          <p className="text-gray-500 text-sm max-w-xl mx-auto leading-relaxed mb-3">
            我们是纯线上婚恋交友平台，价格实惠，只服务真实、善良、有缘分的人。
            <br />
            <span className="text-emerald-600 font-semibold">工厂会员前3个月免费</span> · <span className="text-emerald-600 font-semibold">分销商永久免费</span>
          </p>
          <div className="flex flex-wrap justify-center gap-3 mb-2">
            {['工厂会员前3月免费', '分销商永久免费', '真实认证会员', '全球800城市覆盖'].map((tag, i) => (
              <span key={i} className="inline-flex items-center gap-1 bg-rose-50 text-rose-600 border border-rose-100 rounded-full px-3 py-1 text-xs font-medium">
                <i className="ri-check-line"></i>{tag}
              </span>
            ))}
          </div>
        </div>

        {/* Two Plan Cards Side by Side */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto mb-10">
          {/* Factory/Basic */}
          <div className="relative bg-white border-2 border-red-100 rounded-2xl overflow-hidden hover:-translate-y-1 transition-all duration-300">
            <div className="absolute top-4 right-4 bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full">
              前3月免费
            </div>
            <div className="bg-gradient-to-br from-red-700 to-red-600 p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl">
                  <i className="ri-building-line text-white text-xl"></i>
                </div>
                <div>
                  <div className="text-white/70 text-xs">FACTORY</div>
                  <div className="text-white font-bold">工厂会员</div>
                </div>
              </div>
              <div className="text-2xl font-bold text-white">前3个月免费</div>
              <div className="text-white/60 text-xs mt-1">第4个月起 ¥5/月</div>
            </div>
            <div className="p-5">
              <ul className="flex flex-col gap-2 mb-5">
                {['前3个月完全免费', '每日10次喜欢', '基础搜索筛选', '每日5条消息', '查看谁喜欢了您'].map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                    <i className="ri-check-line text-green-500 text-sm flex-shrink-0"></i>{f}
                  </li>
                ))}
              </ul>
              <Link
                to="/membership"
                className="block w-full text-center py-2.5 bg-red-600 text-white rounded-full text-sm font-semibold hover:bg-red-700 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-gift-line mr-1"></i>免费开始
              </Link>
            </div>
          </div>

          {/* Gold */}
          <div className="relative bg-white border-2 border-amber-300 rounded-2xl overflow-hidden hover:-translate-y-1 transition-all duration-300">
            <div className="absolute top-4 right-4 bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full">
              推荐
            </div>
            <div className="bg-gradient-to-br from-amber-600 to-yellow-500 p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl">
                  <i className="ri-vip-crown-line text-white text-xl"></i>
                </div>
                <div>
                  <div className="text-white/70 text-xs">GOLD</div>
                  <div className="text-white font-bold">黄金会员</div>
                </div>
              </div>
              <div className="flex items-end gap-1">
                <span className="text-white/70 text-sm">¥</span>
                <span className="text-4xl font-bold text-white">10</span>
                <span className="text-white/70 text-sm mb-1">/月</span>
              </div>
            </div>
            <div className="p-5">
              <ul className="flex flex-col gap-2 mb-5">
                {['无限次喜欢 + 无限消息', '无限视频通话', '置顶展示（曝光+300%）', '全球800城市搜索', '专属情感顾问', '学历优先智能匹配'].map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                    <i className="ri-check-line text-amber-500 text-sm flex-shrink-0"></i>{f}
                  </li>
                ))}
              </ul>
              <Link
                to="/membership"
                className="block w-full text-center py-2.5 bg-gradient-to-r from-amber-500 to-yellow-500 text-white rounded-full text-sm font-semibold hover:from-amber-600 hover:to-yellow-600 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-vip-crown-line mr-1"></i>升级黄金会员
              </Link>
            </div>
          </div>
        </div>

        {/* Payment Methods */}
        <div className="text-center">
          <p className="text-xs text-gray-400 mb-3">支持多种支付方式</p>
          <div className="flex items-center justify-center gap-5 flex-wrap">
            {[
              { icon: 'ri-wechat-pay-line', label: '微信支付', color: 'text-green-600' },
              { icon: 'ri-bank-card-line', label: '银行卡', color: 'text-gray-600' },
              { icon: 'ri-visa-line', label: 'Visa', color: 'text-gray-600' },
              { icon: 'ri-mastercard-line', label: 'Mastercard', color: 'text-gray-600' },
              { icon: 'ri-paypal-line', label: 'PayPal', color: 'text-sky-600' },
            ].map((p, i) => (
              <div key={i} className="flex items-center gap-1.5 text-xs text-gray-500">
                <i className={`${p.icon} ${p.color} text-xl`}></i>
                <span>{p.label}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-3">
            <i className="ri-lock-line text-green-500 mr-1"></i>
            SSL加密支付 · 7天退款保障 · 随时取消
          </p>
        </div>
      </div>
    </section>
  );
}
